import { EditorState, Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

import { debounce, type DebouncedFunction } from '@/lib/debounce'
import { AiSuggestionOptions } from '.'
import { AiSuggestionRejection, Suggestion } from './types'
import {
  defaultChunkHtmlFunction,
  defaultResolver,
  generateHash,
  getHtmlFromChunks,
} from './utils'

export interface AiSuggestionProsemirrorPluginState {
  suggestions: Suggestion[]
  rejections: AiSuggestionRejection[]
  selectedSuggestionId: string | null
  loading: boolean
  error: string | null
}

export const aiSuggestionPluginKey = new PluginKey<AiSuggestionProsemirrorPluginState>('aiSuggestion')

export function AiSuggestionPlugin(options: AiSuggestionOptions) {
  return new Plugin<AiSuggestionProsemirrorPluginState>({
    key: aiSuggestionPluginKey,

    state: {
      init: () => ({
        suggestions: options.initialSuggestions || [],
        rejections: options.initialRejections || [],
        selectedSuggestionId: null,
        loading: false,
        error: null,
      }),
      apply: (tr, value) => {
        if (options.disabled) return value;
        const meta = tr.getMeta(aiSuggestionPluginKey)
        // console.log('[AiSuggestion] apply called', { meta, currentState: value })

        let pluginState: AiSuggestionProsemirrorPluginState = { ...value }

        if (meta?.type === 'load') {
          console.log('[AiSuggestion] Handling load request')
          pluginState = {
            ...pluginState,
            loading: true,
            error: null,
            suggestions: [] // Clear suggestions when loading
          }
        } else if (meta?.setSuggestions) {
          // console.log('[AiSuggestion] setSuggestions meta received:', {
          //   suggestions: meta.suggestions,
          //   loading: meta.loading,
          //   error: meta.error
          // })
          pluginState = {
            ...pluginState,
            suggestions: meta.suggestions || [],
            loading: meta.loading ?? false,
            error: meta.error ?? null,
          }
          console.log('[AiSuggestion] new plugin state after setSuggestions:', pluginState)
        }

        // Handle suggestion selection
        if ('selectSuggestion' in (meta || {})) {
          console.log('[AiSuggestion] selectSuggestion meta received:', meta.suggestionId)
          pluginState = {
            ...pluginState,
            selectedSuggestionId: meta.suggestionId
          }
        }

        // Handle suggestion application
        if (meta?.applySuggestion) {
          const { suggestionId } = meta.applySuggestion
          pluginState = {
            ...pluginState,
            suggestions: pluginState.suggestions.filter((s: Suggestion) => s.id !== suggestionId),
            selectedSuggestionId: pluginState.selectedSuggestionId === suggestionId ? null : pluginState.selectedSuggestionId
          }
        }

        if (meta?.setAiSuggestionRejections) {
          pluginState = { ...pluginState, rejections: meta.rejections }
        }

        if (meta?.applySuggestion) {
          const { suggestionId, replacementOptionId } = meta.applySuggestion
          const suggestion = value.suggestions.find((s: Suggestion) => s.id === suggestionId)

          if (suggestion) {
            const replacement = replacementOptionId
              ? suggestion.replacementOptions.find((r) => r.id === replacementOptionId)
              : suggestion.replacementOptions[0]

            if (replacement) {
              const { from, to } = suggestion.deleteRange
              const { addText, addSlice } = replacement

              if (meta.applySuggestion.format === 'rich-text' && addSlice) {
                tr.replace(from, to, addSlice)
              } else {
                tr.insertText(addText, from, to)
              }
            }
          }
        }

        if (meta?.rejectSuggestion) {
          const { suggestionId } = meta.rejectSuggestion
          const suggestion = value.suggestions.find((s: Suggestion) => s.id === suggestionId)

          if (suggestion) {
            suggestion.isRejected = true
          }
        }

        if (tr.docChanged) {
          const mappedSuggestions = pluginState.suggestions.map((suggestion: Suggestion) => {
            const from = tr.mapping.map(suggestion.deleteRange.from)
            const to = tr.mapping.map(suggestion.deleteRange.to)

            return { ...suggestion, deleteRange: { from, to } }
          })

          pluginState = {
            ...pluginState,
            suggestions: mappedSuggestions
          }
        }

        return pluginState
      },
    },

    props: {
      decorations(state: EditorState) {
        if (options.disabled) return DecorationSet.empty;
        const pluginState = this.getState(state)
        if (!pluginState) return DecorationSet.empty
        const { suggestions, selectedSuggestionId } = pluginState
        const decorations: Decoration[] = []

        suggestions.forEach(suggestion => {
          if (suggestion.isRejected) {
            return
          }

          const isSelected = selectedSuggestionId === suggestion.id

          const defaultDecorations = [
            Decoration.inline(suggestion.deleteRange.from, suggestion.deleteRange.to, {
              class: `tiptap-ai-suggestion ${isSelected ? 'tiptap-ai-suggestion--selected' : ''} cursor-pointer`,
              style: `--tiptap-ai-suggestion-color: ${suggestion.rule.color}; --tiptap-ai-suggestion-background-color: ${suggestion.rule.backgroundColor}`,
              'data-suggestion-id': suggestion.id
            }),
          ]

          const customDecorations = options.getCustomSuggestionDecoration({
            suggestion,
            isSelected,
            getDefaultDecorations: () => defaultDecorations,
          })

          decorations.push(...customDecorations)
        })

        return DecorationSet.create(state.doc, decorations)
      },
    },

    view: view => {
      // Add click handler to the editor
      view.dom.addEventListener('click', (e: MouseEvent) => {
        if (options.disabled) return;
        if (!options.resolver) {
          return
        }
        const target = e.target as HTMLElement
        const suggestionId = target.getAttribute('data-suggestion-id')
        if (suggestionId) {
          e.preventDefault()
          const currentState = aiSuggestionPluginKey.getState(view.state)
          const newId = currentState?.selectedSuggestionId === suggestionId ? null : suggestionId
          view.dispatch(view.state.tr.setMeta(aiSuggestionPluginKey, { selectSuggestion: true, suggestionId: newId, type: 'selectSuggestion' }))
        }
      })

      const loadSuggestions = async () => {
        if (options.disabled) return;
        const {
          resolver,
          chunkHtmlFunction = defaultChunkHtmlFunction,
          chunkSize,
          appId,
          modelName,
          rules,
          context,
          token,
          baseUrl,
        } = options

        if (!resolver) {
          // console.debug('[AiSuggestion] No resolver provided')
          return
        }

        console.log('[AiSuggestion] loadSuggestions called')
        
        try {
          // Get the HTML content
          const htmlChunks = chunkHtmlFunction({
            html: view.dom.innerHTML,
            chunkSize,
          })

          // console.log('[AiSuggestion] Calling resolver with options:', {
          //   rules,
          //   htmlChunks,
          //   context
          // })

          const suggestions = await resolver({
            editor: view,
            chunkHtmlFunction,
            chunkSize,
            getHtmlFromChunks,
            generateHash,
            defaultResolver,
            appId,
            modelName,
            rules,
            context: context ?? undefined,
            token,
            baseUrl,
            htmlChunks,
          })

          console.log('[AiSuggestion] Resolver returned suggestions:', suggestions)

          // Update with suggestions in a single transaction
          const tr = view.state.tr.setMeta(aiSuggestionPluginKey, {
            setSuggestions: true,
            suggestions,
            loading: false,
            error: null
          })

          view.dispatch(tr)
        } catch (error) {
          console.error('[AiSuggestion] Failed to load suggestions:', error)
          const errorTr = view.state.tr.setMeta(aiSuggestionPluginKey, {
            setSuggestions: true,
            suggestions: [],
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load suggestions'
          })
          view.dispatch(errorTr)
        }
      }

      const debouncedLoad: DebouncedFunction<typeof loadSuggestions> = debounce(loadSuggestions, options.debounceTimeout)

      if (options.loadOnStart && !options.disabled) {
        debouncedLoad()
      }

      return {
        update: (view, prevState) => {
          if (options.disabled) return;
          // disable during streaming
          
          const pluginState = aiSuggestionPluginKey.getState(view.state)
          const prevPluginState = aiSuggestionPluginKey.getState(prevState)
          const meta = view.state.tr.getMeta(aiSuggestionPluginKey)
          
          // console.log('[AiSuggestion] update called', { 
          //   meta,
          //   pluginState,
          //   prevPluginState,
          //   loading: pluginState?.loading,
          //   docChanged: !view.state.doc.eq(prevState.doc)
          // })

          // Handle explicit load requests
          if (meta?.type === 'load') {
            console.log('[AiSuggestion] Load request detected, setting loading state')
            // The actual load will be triggered by the loading transition below
            return
          }

          // Detect transition from loading: false -> true
          if (pluginState?.loading && !prevPluginState?.loading) {
            console.log('[AiSuggestion] Detected loading transition, calling loadSuggestions')
            loadSuggestions()
            return
          }

          // Handle automatic reloads
          if (options.reloadOnUpdate && !view.state.doc.eq(prevState.doc)) {
            if (pluginState?.loading) {
              console.log('[AiSuggestion] Already loading, skipping automatic reload')
              return
            }
            console.log('[AiSuggestion] Document changed, calling debouncedLoad suggestions')
            debouncedLoad()
          }
        },
        destroy: () => {
          // console.log('[AiSuggestion] Destroying plugin, cleaning up debounced calls')
          // Cleanup any pending debounced calls
          debouncedLoad.cancel?.()
        }
      }
    },
  })
}
